/**
 * CORE-896 — экран согласия перед первым использованием ИИ-чата.
 *
 * Реджект 1.0.0 (14) по Guidelines 5.1.1(i) / 5.1.2(i): приложение передаёт
 * данные стороннему ИИ-сервису, не называя ни состав данных, ни получателя, и
 * не спрашивая разрешения. Apple прямо пишет, что записи в Политике
 * конфиденциальности для этого недостаточно — нужен отдельный экран.
 *
 * Почему свой переключатель RU/EN, а не язык приложения: `pickInitialLanguage`
 * (CORE-573) отдаёт русский любому устройству, кроме испаноязычного, — то есть
 * и англоязычному iPad ревьюера. Экран согласия, который ревьюер не может
 * прочитать, не является раскрытием. Ровно та же причина, по которой
 * переключатель появился в `LegalDocsModal` (CORE-840); язык приложения он не
 * трогает.
 *
 * Тексты — прямо здесь, а не в i18n-ресурсах: обе редакции должны быть видны
 * рядом при вычитке юристом и не могут разъехаться по разным файлам.
 *
 * CORE-1661 — `{operator}` в тексте: название оператора подставляется по
 * юрисдикции (`legal/operator.ts`), а не по языку экрана.
 *
 * CORE-1401, возврат QA 23.10 — испанского варианта у экрана нет намеренно, как
 * и раньше: текст согласия читает юрист, и редакций у него ровно столько, сколько
 * вычитано (RU и EN, у каждой — вариант с оператором по юрисдикции). Испанский
 * интерфейс видит английский текст.
 */

import React, { useState } from 'react';
import { View, ScrollView, StyleSheet, Text, TouchableOpacity, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Typography, Button } from '../ui';
import { aiNoticeLinkColor, aiNoticeLinkProps } from './AiNoticeLink';
import { useTheme } from '../../context/ThemeContext';
import { fonts, typography } from '../../styles/typography';
import { currentOperator } from '../../legal/accepted';
import { withOperator } from '../../legal/operator';
import { pointForOperator } from '../../legal/aiDisclosure';

type Lang = 'ru' | 'en';

interface Copy {
  title: string;
  intro: string;
  /**
   * `short` — то, что видно сразу; `body` — та же мысль полностью, под
   * раскрытием. `detailOnly` убирает пункт из свёрнутого вида целиком: он
   * относится к делу, но не к решению, которое человек здесь принимает.
   */
  points: {
    icon: keyof typeof Ionicons.glyphMap;
    title: string;
    short: string;
    body: string;
    detailOnly?: boolean;
    /**
     * CORE-1567 — текст пункта для оператора вне контура РФ (`intl`).
     * В российском контуре получатель (OpenAI) не называется до подачи
     * уведомлений в РКН (решение владельца 30.10 по ответу юриста 18); вне
     * контура раскрытие прежнее.
     */
    intl?: { short: string; body: string };
  }[];
  policyShort: string;
  policyHint: string;
  /** CORE-1401 — подпись ссылки на «Уведомление об ИИ». */
  policyLink: string;
  /** CORE-1401 — вход в «Что я о тебе помню» из «Данные и ИИ». */
  memory: string;
  /** Согласие не записалось на сервере — чат не открыт, и это надо сказать. */
  acceptFailed: string;
  more: string;
  less: string;
  accept: string;
  decline: string;
  revoke: string;
  forget: string;
  grantedNote: string;
  langSwitchLabel: string;
}

const COPY: Record<Lang, Copy> = {
  ru: {
    title: 'Прежде чем начать разговор',
    intro: 'Ответы в этом чате формирует сторонний ИИ-сервис.',
    points: [
      {
        icon: 'chatbubble-ellipses-outline',
        title: 'Что передаётся',
        // CORE-1401, возвраты QA 24.09, 06.10 и 23.10 — экран называл только
        // имя и открытый экран, а в промпт по коду уходит и профиль, и снимок
        // платформы (awesome-chat-api `prompt_context`). Перечень сверен с ним
        // и с п. 3.1 Уведомления; то, чего код не передаёт (дата регистрации),
        // здесь не названо, хотя Уведомление это допускает.
        short: 'Твои сообщения здесь — текст, голосовые, фото и файлы, — профиль (имя, «О себе», намерение, город, часовой пояс) и то, как ты пользуешься платформой: подписки, избранное, календарь, круги, активность, открытый экран.',
        body:
          'Текст, который ты пишешь в этом чате, голосовые сообщения (их запись переводится в текст), фото и файлы, которые ты прикрепляешь, и предыдущие сообщения этого же разговора. Из профиля — имя, «О себе», намерение и город; из настроек — язык, часовой пояс и регион. О том, как ты пользуешься платформой, — подписки на мастеров и Live-сессии, избранное, события календаря, круги, число друзей (без их имён), счётчики пройденных практик и посещённых Live-сессий, уровень на «Пути Света», интересы, которые платформа заметила по твоим действиям, и то, какой экран приложения у тебя открыт. И то, что помощница запомнила о тебе в прошлых разговорах. Всё это — чтобы ответ был по делу. Почта, телефон, контакты, геопозиция и платёжные данные не передаются; из галереи уходят только те фото, которые ты сама выбираешь.',
      },
      {
        icon: 'send-outline',
        title: 'Кому',
        short: 'Ответ готовит сервис искусственного интеллекта по поручению Agapella.',
        body:
          'Сообщение уходит на сервер Agapella ({operator}), а он обращается к сервису искусственного интеллекта, который формирует ответ и переводит голосовые сообщения в текст. Сервис обрабатывает данные по нашему поручению и не использует их для обучения своих моделей.',
        intl: {
          short: 'Ответ готовит OpenAI по поручению Agapella.',
          body:
            'Сообщение уходит на сервер Agapella ({operator}), а он обращается к OpenAI, L.L.C. (США) — сервису, который формирует ответ и переводит голосовые сообщения в текст. OpenAI обрабатывает данные по нашему поручению и не использует их для обучения своих моделей.',
        },
      },
      {
        icon: 'bookmark-outline',
        title: 'Что остаётся в памяти',
        short: 'Помощница помнит то, что ты о себе рассказываешь. Стереть можно в любой момент.',
        body:
          'Помощница запоминает то, что ты сама о себе рассказываешь, чтобы не спрашивать заново. Сохранённые заметки видны в «Что я о тебе помню», а стереть всё разом можно в Настройках, пункт «Данные и ИИ».',
      },
      {
        // Публикации к этому решению отношения не имеют — проверка идёт в любом
        // случае. В свёрнутом виде это лишняя тревога на ровном месте.
        detailOnly: true,
        icon: 'shield-checkmark-outline',
        title: 'Отдельно — проверка публикаций',
        short: '',
        body:
          'Текст и изображения, которые публикуются на платформе, тот же сервис проверяет на недопустимый контент до публикации. Это условие публикации и от выбора здесь не зависит.',
      },
    ],
    policyShort: 'Согласие можно отозвать в любой момент.',
    // CORE-1401, возврат QA 23.10 — путь «Настройки → Агапелла» верен только в
    // приложении; на вебе у обычного пользователя пункт лежит во вкладке
    // «Дополнительно». Здесь — то, что верно везде; путь в Политике (п. 10.6)
    // — юридический текст, его меняет не этот экран.
    policyHint:
      'Согласие можно отозвать в любой момент в Настройках, пункт «Данные и ИИ». Подробности — в Уведомлении об использовании ИИ.',
    policyLink: 'Уведомление об использовании ИИ и обработке данных',
    memory: 'Что я о тебе помню',
    acceptFailed: 'Не получилось сохранить согласие, чат пока закрыт. Проверь соединение и попробуй ещё раз.',
    more: 'Подробнее о данных',
    less: 'Свернуть',
    accept: 'Принять и продолжить',
    decline: 'Не сейчас',
    revoke: 'Отозвать согласие и удалить данные',
    forget: 'Удалить данные ИИ-чата: память, профиль и переписку',
    grantedNote: 'Согласие дано. Чат с ИИ-помощницей доступен.',
    langSwitchLabel: 'Read in English',
  },
  en: {
    title: 'Before we start talking',
    intro: 'Replies in this chat are generated by a third-party AI service.',
    points: [
      {
        icon: 'chatbubble-ellipses-outline',
        title: 'What is sent',
        short: 'Your messages here — text, voice, photos and files — your profile (name, "About me", intention, city, time zone) and how you use the platform: subscriptions, favorites, calendar, circles, activity, the screen you have open.',
        body:
          'The text you type in this chat, your voice messages (the recording is converted to text), the photos and files you attach, and the earlier messages of this same conversation. From your profile: your name, "About me", intention and city; from your settings: language, time zone and region. About how you use the platform: the teachers and live sessions you follow, your favorites, calendar events, circles, the number of your friends (not their names), counts of practices completed and live sessions attended, your level on the Path of Light, the interests the platform has noticed from what you do, and which app screen you have open. And what the assistant remembered about you in earlier conversations. All of this is so the reply is relevant. No email, phone number, contacts, location or payment data is sent; from your photo library, only the photos you choose yourself are sent.',
      },
      {
        icon: 'send-outline',
        title: 'Who it is sent to',
        short: 'An artificial-intelligence service writes the reply, on Agapella’s instructions.',
        body:
          'The message goes to the Agapella server ({operator}), which calls an artificial-intelligence service that generates the reply and converts voice messages to text. The service processes the data on our instructions and does not use it to train its models.',
        intl: {
          short: 'OpenAI writes the reply, on Agapella’s instructions.',
          body:
            'The message goes to the Agapella server ({operator}), which calls OpenAI, L.L.C. (USA) — the service that generates the reply and converts voice messages to text. OpenAI processes the data on our instructions and does not use it to train its models.',
        },
      },
      {
        icon: 'bookmark-outline',
        title: 'What is remembered',
        short: 'The assistant remembers what you tell it about yourself. You can delete it any time.',
        body:
          'The assistant remembers what you tell it about yourself, so it does not have to ask again. Saved notes are listed in "What I remember about you", and you can delete all of it at once in Settings, under "Data and AI".',
      },
      {
        // Publishing is screened either way — this does not bear on the choice
        // being made here, and collapsed it only reads as one more warning.
        detailOnly: true,
        icon: 'shield-checkmark-outline',
        title: 'Separately: content safety',
        short: '',
        body:
          'Text and images published on the platform are screened for objectionable content by the same service before they go live. That is a condition of publishing and does not depend on your choice here.',
      },
    ],
    policyShort: 'You can withdraw this consent at any time.',
    policyHint:
      'You can withdraw this consent at any time in Settings, under "Data and AI". Details are in the AI Notice.',
    // Перевода у Уведомления нет — говорим об этом до того, как человек его откроет.
    policyLink: 'AI and data processing notice (in Russian)',
    memory: 'What I remember about you',
    acceptFailed: 'Your consent could not be saved, so the chat is still closed. Check your connection and try again.',
    more: 'More about your data',
    less: 'Show less',
    accept: 'Accept and continue',
    decline: 'Not now',
    revoke: 'Withdraw consent and delete my data',
    forget: 'Delete your AI chat data: memory, profile and the conversation',
    grantedNote: 'Consent granted. The AI companion chat is available.',
    langSwitchLabel: 'Показать по-русски',
  },
};

interface Props {
  /**
   * `gate` — вместо чата, до первой отправки; `review` — та же справка из
   * настроек, где действие обратное: отозвать уже данное согласие.
   */
  mode?: 'gate' | 'review';
  /** Только для `review`: текущее состояние согласия. */
  granted?: boolean;
  /** Язык приложения — с него стартует переключатель. */
  appLanguage?: string;
  /**
   * CORE-1660 — `lang`: на каком языке экран был открыт в момент согласия.
   *
   * CORE-1401 — может вернуть промис: отказ сервера (сеть, 400 на неизвестную
   * редакцию) экран показывает сам. Раньше `void accept()` глотал ошибку, и
   * «Принять» выглядело кнопкой, которая ничего не делает.
   */
  onAccept: (lang: Lang) => void | Promise<void>;
  onDecline: () => void;
  /** Только для `review`: стереть память и собранный профиль. */
  onForgetMemory?: () => void;
  forgettingMemory?: boolean;
  /**
   * CORE-1401 — как открыть документ об обработке данных (Уведомление об ИИ).
   *
   * На вебе это ссылка в новой вкладке, и колбэк не нужен. На нативе по
   * умолчанию — переход через `navigateTo` из `rootNavigation`, а НЕ через
   * `useNavigation`. Ревью 06.10 поймало ровно это: экран согласия рисуется и
   * ВНЕ `NavigationContainer` — виджет Агапеллы смонтирован в `App.tsx` после
   * него и показывает согласие как гейт. Там хук бросает исключение, то есть
   * панель падала бы на рендере у того, кто согласие ещё не дал. Контейнерный
   * ref молча ничего не делает, пока навигатор не готов, — именно за этим он и
   * заведён (см. его докстринг про CORE-509).
   *
   * Колбэк нужен нативным поверхностям, которые должны что-то закрыть перед переходом:
   * из модалки настроек переход сменил бы экран ПОД ней, и ссылка выглядела бы
   * мёртвой, пока лист не закрыт.
   */
  onOpenPolicy?: () => void;
  /** Только для `review`: открыть «Что я о тебе помню» (CORE-1401, возврат QA 23.10). */
  onOpenMemory?: () => void;
}

export const AiDataConsent: React.FC<Props> = ({
  mode = 'gate',
  granted = false,
  appLanguage,
  onAccept,
  onDecline,
  onForgetMemory,
  forgettingMemory = false,
  onOpenPolicy,
  onOpenMemory,
}) => {
  const { colors, isDark } = useTheme();
  // Кнопки прижаты к нижнему краю, а под ними на Android живёт полоса
  // жестовой навигации: фиксированный отступ клал «Не сейчас» вплотную к ней.
  // На вебе inset нулевой, так что верстка там не меняется.
  const insets = useSafeAreaInsets();
  const [lang, setLang] = useState<Lang>(() =>
    (appLanguage || 'ru').toLowerCase().startsWith('ru') ? 'ru' : 'en',
  );
  const copy = COPY[lang];
  const [operator] = useState(currentOperator);
  const points = copy.points
    .map((p) => pointForOperator(p, operator))
    .map((p) => ({ ...p, body: withOperator(p.body, operator, lang) }));
  const isReview = mode === 'review';
  // Справка из настроек открывается ради подробностей — там разворачивать
  // нечего. Гейт стартует свёрнутым.
  const [expanded, setExpanded] = useState(isReview);
  const [accepting, setAccepting] = useState(false);
  const [acceptFailed, setAcceptFailed] = useState(false);

  const handleAccept = async () => {
    if (accepting) return;
    setAcceptFailed(false);
    setAccepting(true);
    try {
      await onAccept(lang);
    } catch {
      setAcceptFailed(true);
    } finally {
      setAccepting(false);
    }
  };

  return (
    <View style={[styles.wrap, { backgroundColor: colors.backgroundAlt }]} testID="aiDataConsent">
      <ScrollView contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>
        <View style={styles.headRow}>
          <View style={[styles.iconCircle, { backgroundColor: `${colors.solar}1F` }]}>
            <Ionicons name="sparkles-outline" size={26} color={colors.solar} />
          </View>
          <TouchableOpacity
            testID="aiDataConsentLanguage"
            onPress={() => setLang((v) => (v === 'ru' ? 'en' : 'ru'))}
            hitSlop={10}
            accessibilityRole="button"
            accessibilityLabel={copy.langSwitchLabel}
            style={[styles.langBtn, { borderColor: colors.border }]}
          >
            <Typography variant="caption" style={[styles.langLabel, { color: colors.textPrimary }]}>
              {lang === 'ru' ? 'EN' : 'RU'}
            </Typography>
          </TouchableOpacity>
        </View>

        <Typography variant="h3" style={[styles.title, { color: colors.textPrimary }]}>
          {copy.title}
        </Typography>
        <Typography variant="body" style={[styles.intro, { color: colors.textSecondary }]}>
          {copy.intro}
        </Typography>

        {/* CORE-1401, возврат QA 23.10 — «Что я о тебе помню» обещан на этом
            экране и в Политике (п. 10.6), а войти в него можно было только по
            прямому адресу. Вход — здесь, в «Данные и ИИ»: туда человек и
            приходит, когда хочет знать, что о нём помнят. Стоит сверху: справка
            открывается развёрнутой, и внизу прокрутки пункт никто не находил. */}
        {isReview && onOpenMemory ? (
          <TouchableOpacity
            testID="aiDataConsentMemory"
            onPress={onOpenMemory}
            hitSlop={8}
            accessibilityRole="button"
            style={styles.memoryRow}
          >
            <Ionicons name="bookmark-outline" size={16} color={aiNoticeLinkColor(isDark)} />
            <Typography variant="bodySmall" style={[styles.moreLabel, styles.memoryLabel, { color: aiNoticeLinkColor(isDark) }]}>
              {copy.memory}
            </Typography>
          </TouchableOpacity>
        ) : null}

        {points
          .filter((point) => expanded || !point.detailOnly)
          .map((point) => (
            <View key={point.title} style={styles.point}>
              <Ionicons name={point.icon} size={18} color={colors.solar} style={styles.pointIcon} />
              <View style={styles.pointText}>
                <Typography variant="body" style={[styles.pointTitle, { color: colors.textPrimary }]}>
                  {point.title}
                </Typography>
                <Typography variant="bodySmall" style={[styles.pointBody, { color: colors.textSecondary }]}>
                  {expanded ? point.body : point.short}
                </Typography>
              </View>
            </View>
          ))}

        {/* CORE-1401 (замер 23.10) — подписи и «Подробнее о данных» на этой
            плите тоже не держали 4.5: textMuted 4.4:1, solar 2.0:1. Подпись —
            вторичным текстом (4.8:1 / 8.2:1), раскрытие — акцентным, как ссылка. */}
        <Typography variant="caption" style={[styles.hint, { color: colors.textSecondary }]}>
          {expanded ? copy.policyHint : copy.policyShort}
        </Typography>

        {/* CORE-1401 — ссылка на «Уведомление об использовании ИИ и обработке
            данных в ИИ-чате» (ред. 01.10.2026). До возврата QA 23.10 она вела в
            общую Политику: Уведомления в продукте не было. Теперь документ
            встроен (`LegalDocScreen`, docId `ai-notice`), и его же открывает
            строка под полем ввода в самом чате (AiNoticeLink).

            Отдельной строкой, а не ссылкой внутри абзаца: разметка внутри
            строки потребовала бы знать смещения в каждом языке, а языков здесь
            два и текст у них разной длины. Отдельная строка переживает перевод.

            Цвет — акцентный текст CORE-1733, а не `colors.link`: синий на
            бежевой плите давал 2.72:1 при 12 px. На вебе — настоящая ссылка в
            новой вкладке, как у гейта регистрации: решение не теряется.
            `onOpenPolicy` нужен только нативу (модалку настроек сначала
            закрыть, потом уйти). */}
        <Text
          testID="aiDataPolicyLink"
          accessibilityRole="link"
          accessibilityLabel={copy.policyLink}
          style={[typography.caption, styles.policyLink, { color: aiNoticeLinkColor(isDark) }]}
          {...(onOpenPolicy && Platform.OS !== 'web'
            ? { onPress: onOpenPolicy }
            : aiNoticeLinkProps())}
        >
          {copy.policyLink}
        </Text>

        {/* Согласие должно быть информированным — значит полный текст обязан
            быть здесь, на этом же экране, в одно касание. Но разворачивать его
            по умолчанию значит встречать человека полутора экранами
            предупреждений там, где он хотел поговорить. Свёрнутый вид несёт
            то, от чего зависит решение: что уходит, кому и что это отзывается.
            В `review` — это справка из настроек, её открывают именно за
            подробностями, поэтому там всё сразу. */}
        {!isReview ? (
          <TouchableOpacity
            testID="aiDataConsentToggleDetails"
            onPress={() => setExpanded((v) => !v)}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityState={{ expanded }}
          >
            <Typography variant="bodySmall" style={[styles.moreLabel, { color: aiNoticeLinkColor(isDark) }]}>
              {expanded ? copy.less : copy.more}
            </Typography>
          </TouchableOpacity>
        ) : null}

        {isReview && granted ? (
          <Typography variant="caption" style={[styles.hint, { color: colors.textSecondary }]}>
            {copy.grantedNote}
          </Typography>
        ) : null}

      </ScrollView>

      {/* Кнопки живут ВНЕ прокрутки: текста здесь на полтора экрана телефона, и
          согласие, до которого надо доскроллить, читается как «выхода нет» —
          в том числе ревьюером App Review. */}
      <View
        style={[
          styles.actions,
          { borderTopColor: colors.border },
          Platform.OS !== 'web' && { paddingBottom: Math.max(insets.bottom, 10) + 14 },
        ]}
      >
        {isReview && granted ? (
          <>
            <Button variant="outline" onPress={onDecline} testID="aiDataConsentRevoke" style={styles.btn}>
              {copy.revoke}
            </Button>
            {/* CORE-1578 — отзыв согласия теперь И закрывает чат, И запускает
                уничтожение накопленного: Политика ПДн п.17 требует уничтожить
                данные после отзыва. Отдельная кнопка удаления всё равно нужна —
                стереть накопленное, не отзывая согласия и не закрывая чат, это
                другое желание, и без него «удалить мои данные» осталось бы
                невыполнимым для того, кто хочет продолжать разговор (5.1.2(i)). */}
            {onForgetMemory ? (
              <Button
                variant="ghost"
                onPress={onForgetMemory}
                isLoading={forgettingMemory}
                testID="aiDataConsentForget"
                style={styles.btn}
              >
                {copy.forget}
              </Button>
            ) : null}
          </>
        ) : (
          <>
            {acceptFailed ? (
              <Text
                testID="aiDataConsentAcceptFailed"
                accessibilityRole="alert"
                // Терракотовый текст ошибки — тот же, что у ошибок переписки v3
                // (#9A421F в светлой): `colors.error` на бежевом не держит 4.5.
                style={[typography.caption, styles.hint, { color: aiNoticeLinkColor(isDark), textAlign: 'center' }]}
              >
                {copy.acceptFailed}
              </Text>
            ) : null}
            <Button
              variant="convex-fire"
              onPress={() => { void handleAccept(); }}
              isLoading={accepting}
              testID="aiDataConsentAccept"
              style={styles.btn}
            >
              {copy.accept}
            </Button>
            {!isReview ? (
              <Button variant="ghost" onPress={onDecline} testID="aiDataConsentDecline" style={styles.btn}>
                {copy.decline}
              </Button>
            ) : null}
          </>
        )}
      </View>
    </View>
  );
};

/** Экран общий для веба и мобильного; расхождения в вёрстке — только под этим. */
const NATIVE = Platform.OS !== 'web';

const styles = StyleSheet.create({
  wrap: {
    flex: 1,
  },
  scroll: {
    paddingHorizontal: 22,
    paddingTop: 18,
    paddingBottom: 28,
    maxWidth: 560,
    width: '100%',
    alignSelf: 'center',
  },
  headRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  iconCircle: {
    width: 52,
    height: 52,
    borderRadius: 26,
    alignItems: 'center',
    justifyContent: 'center',
  },
  langBtn: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
  },
  langLabel: {
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  // Кегли на нативе на 1 pt меньше базовых: на телефоне этот текст занимает
  // полтора экрана, и шкала, рассчитанная на десктоп, читается как крупная.
  // Веб сохраняет прежние размеры — там ширина колонки другая.
  title: {
    fontFamily: fonts.heading,
    fontWeight: 'normal',
    fontSize: NATIVE ? 22 : 23,
    letterSpacing: -0.4,
    marginBottom: 8,
  },
  intro: {
    ...(NATIVE && { fontSize: 19 }), // body = 20
    lineHeight: 22,
    marginBottom: 20,
  },
  point: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  pointIcon: {
    marginTop: 3,
  },
  pointText: {
    flex: 1,
  },
  pointTitle: {
    ...(NATIVE && { fontSize: 19 }), // body = 20
    fontWeight: '600',
    marginBottom: 3,
  },
  pointBody: {
    ...(NATIVE && { fontSize: 17 }), // bodySmall = 18
    lineHeight: 20,
  },
  hint: {
    ...(NATIVE && { fontSize: 15 }), // caption = 16
    lineHeight: 18,
    marginTop: 6,
  },
  moreLabel: {
    marginTop: 12,
    fontWeight: '600',
  },
  policyLink: {
    marginTop: 6,
    alignSelf: 'flex-start',
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  memoryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    marginTop: -4,
    marginBottom: 20,
  },
  memoryLabel: {
    marginTop: 0,
  },
  actions: {
    gap: 10,
    paddingHorizontal: 22,
    paddingTop: 14,
    paddingBottom: Platform.OS === 'web' ? 18 : 26,
    borderTopWidth: StyleSheet.hairlineWidth,
    maxWidth: 560,
    width: '100%',
    alignSelf: 'center',
  },
  btn: {
    width: '100%',
    borderRadius: 14,
    // На нативе фиксированная высота 50 обрезала подпись: Button размера `md`
    // добавляет свои paddingVertical: 16 сверху и снизу, и вместе со строкой
    // 16-го кегля контент не помещался в 50 — у «Принять и продолжить» срезало
    // нижнюю часть букв. minHeight сохраняет ту же высоту у коротких подписей и
    // даёт кнопке вырасти там, где иначе текст обрезается. Веб остаётся как был.
    ...(Platform.OS === 'web'
      ? { height: 50 }
      : { minHeight: 50, paddingVertical: 12 }),
  },
});

export default AiDataConsent;
